@if (isProductDetailsLoading) {
  <app-loading-spinner></app-loading-spinner>
}
@if (!isProductDetailsLoading) {
  <div class="product-details-container animate-fade-in">
    <!-- Header Section -->
    <header class="page-header mb-3">
      <div class="header-left">
        <button class="back-btn" (click)="onBackToProducts()" title="Back to Products">
          <i class="bi bi-arrow-left"></i>
        </button>
        <div class="header-titles">
          <h1 class="product-name">{{ product.name }}</h1>
          <span class="product-category">{{ product.product_category?.name || 'Uncategorized' }} Catalog</span>
        </div>
      </div>
      <div class="header-actions d-flex gap-2">
        <button type="button" class="btn btn-soft-primary" (click)="onManage()" title="Manage Units & Packaging Types">
          <i class="bi bi-sliders me-1"></i>
          <span>Manage</span>
        </button>
        <button type="button" class="btn btn-primary" (click)="onAddPackaging()">
          <i class="bi bi-plus-circle"></i>
          <span>Add Packaging</span>
        </button>
      </div>
    </header>

    <!-- Tab Navigation -->
    <div class="px-3">
      <app-tab-navigation [tabs]="tabs" [selectedTab]="activeTab" (tabChange)="selectTab($event)" />
    </div>

    <div class="p-3">
      <!-- Overview Tab -->
      @if (activeTab === 'overview') {
        <section class="info-panel card">
          <div class="panel-header">
            <h2 class="panel-title">
              <i class="bi bi-info-square me-2"></i>
              Product Properties
            </h2>
          </div>
          <div class="panel-body">
            <div class="properties-list">
              <div class="property-item">
                <label>Chemical Contents</label>
                <div class="value">{{ product.contents || 'General Formulation' }}</div>
              </div>
              <div class="property-item">
                <label>HSN Code</label>
                <div class="value fw-bold">{{ product.hsn_code || 'N/A' }}</div>
              </div>
              <div class="property-item">
                <label>Formulation</label>
                <div class="value">
                  @if (product.formulation_type?.label; as formulation) {
                    <span class="app-badge" [ngClass]="formulationBadgeClass(product)">{{ formulation }}</span>
                  } @else {
                    -
                  }
                </div>
              </div>
              <div class="property-item">
                <label>Company</label>
                <div class="value">{{ product.company?.name || 'N/A' }}</div>
              </div>
            </div>
          </div>
        </section>
      }

      <!-- Packaging Tab -->
      @if (activeTab === 'packaging') {
        <section class="packaging-panel card">
          <div class="panel-body p-0">
            @if (product.product_packaging && product.product_packaging.length > 0) {
              <div class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th style="width: 80px">Image</th>
                      <th>Format</th>
                      <th>Inventory Details</th>
                      <th class="text-end">Dealer Rate (Excl. GST)</th>
                      <th class="text-end">Dealer Rate (Incl. GST)</th>
                      <th class="text-end">MRP</th>
                      <th class="text-center">BOM Status</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (packaging of product.product_packaging; track packaging.id) {
                      <tr class="packaging-row cursor-pointer" (click)="onEditPackaging(packaging)">
                        <td>
                          <div class="packaging-img-wrapper">
                            @if (packaging.image_url) {
                              <img [src]="packaging.image_url" [alt]="packaging.packing_size" />
                            } @else {
                              <i class="bi bi-image text-muted"></i>
                            }
                          </div>
                        </td>
                        <td>
                          <div class="format-info">
                            <span class="size-label">{{ packaging.packing_size }} {{ packaging.unit.short_name }}</span>
                            <span class="type-sub">{{ packaging.packaging_type?.name || 'Standard' }}</span>
                          </div>
                        </td>
                        <td>
                          <div class="units-box">{{ packaging.nos }} Units/Box</div>
                          <div class="gst-info text-muted extra-small">GST: {{ packaging.gst_rate }}%</div>
                        </td>

                        <td class="text-end">
                          <span class="fw-medium">
                            {{ packaging.dealer_rate ? '₹' + (packaging.dealer_rate | number: '1.2-2') : '—' }}
                          </span>
                        </td>
                        <td class="text-end">
                          <span class="fw-medium">
                            {{
                              packaging.dealer_rate_incl_gst
                                ? '₹' + (packaging.dealer_rate_incl_gst | number: '1.2-2')
                                : '—'
                            }}
                          </span>
                        </td>
                        <td class="text-end">
                          <span class="fw-medium">₹{{ packaging.mrp | number: '1.2-2' }}</span>
                        </td>
                        <td class="text-center">
                          @if (packaging.bill_of_material?.materials?.length) {
                            <span class="bom-status active">
                              <i class="bi bi-check-circle-fill me-1"></i>
                              Linked
                            </span>
                          } @else {
                            <span class="bom-status inactive">
                              <i class="bi bi-exclamation-triangle-fill me-1"></i>
                              No BOM
                            </span>
                          }
                        </td>
                        <td class="text-end">
                          <div class="action-group">
                            <button
                              class="btn btn-action-delete"
                              (click)="onDeletePackaging(packaging); $event.stopPropagation()"
                              title="Delete">
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="empty-packaging">
                <div class="empty-icon">
                  <i class="bi bi-box-seam"></i>
                </div>
                <h3>No Packaging Configurations</h3>
                <p>Start by adding the first packaging size for this product Registry.</p>
              </div>
            }
          </div>
        </section>
      }
    </div>
  </div>
}

<!-- Manage Units & Packaging Types Modal -->
<ng-template #manageModal let-modal>
  <div class="modal-content border-0">
    <div class="modal-header border-0 pb-0">
      <h5 class="modal-title fw-bold">Manage Reference Data</h5>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>

    <div class="px-4 pt-2">
      <ul class="nav nav-tabs border-bottom">
        <li class="nav-item">
          <button
            class="nav-link border-0 px-3 py-2"
            [class.active]="manageModalTab === 'units'"
            (click)="manageModalTab = 'units'; newUnitName = ''; newUnitShortName = ''">
            <i class="bi bi-rulers me-1"></i>
            Units
            <span
              [ngClass]="manageModalTab === 'units' ? 'bg-primary' : 'bg-secondary'"
              class="badge ms-1 rounded-pill">
              {{ units.length }}
            </span>
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link border-0 px-3 py-2"
            [class.active]="manageModalTab === 'packaging-types'"
            (click)="manageModalTab = 'packaging-types'; newPackagingTypeName = ''">
            <i class="bi bi-box me-1"></i>
            Packaging Types
            <span
              [ngClass]="manageModalTab === 'packaging-types' ? 'bg-primary' : 'bg-secondary'"
              class="badge ms-1 rounded-pill">
              {{ packagingTypes.length }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div class="modal-body px-4 pt-3 pb-2" style="min-height: 280px; max-height: 420px; overflow-y: auto">
      <!-- Units Tab -->
      @if (manageModalTab === 'units') {
        @if (units.length === 0) {
          <div class="text-center py-4 text-muted small">
            <i class="bi bi-rulers d-block fs-3 mb-2 opacity-50"></i>
            No units yet. Add one below.
          </div>
        } @else {
          <div class="manage-item-list mb-3">
            @for (unit of units; track unit.id) {
              <div class="manage-item">
                <div class="d-flex align-items-center gap-2">
                  <span class="fw-semibold">{{ unit.name }}</span>
                  <span class="badge bg-light text-secondary border fw-normal">{{ unit.short_name }}</span>
                </div>
                <button
                  type="button"
                  class="btn btn-action-delete btn-sm"
                  [disabled]="deletingUnitId === unit.id"
                  (click)="onDeleteUnit(unit.id)">
                  @if (deletingUnitId === unit.id) {
                    <app-loading-spinner [inline]="true"></app-loading-spinner>
                  } @else {
                    <i class="bi bi-trash3"></i>
                  }
                </button>
              </div>
            }
          </div>
        }
        <div class="add-item-form">
          <p class="text-muted small fw-semibold mb-2">Add New Unit</p>
          <div class="d-flex gap-2">
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Full name (e.g. Kilogram)"
              [(ngModel)]="newUnitName" />
            <input
              type="text"
              class="form-control form-control-sm"
              style="max-width: 110px"
              placeholder="Short (e.g. kg)"
              [(ngModel)]="newUnitShortName" />
            <button
              type="button"
              class="btn btn-primary btn-sm text-nowrap"
              [disabled]="!newUnitName.trim() || !newUnitShortName.trim() || isCreatingUnit"
              (click)="onCreateUnit()">
              @if (isCreatingUnit) {
                <app-loading-spinner [inline]="true" [textClass]="'text-white'"></app-loading-spinner>
              } @else {
                <i class="bi bi-plus-lg"></i>
              }
            </button>
          </div>
        </div>
      }

      <!-- Packaging Types Tab -->
      @if (manageModalTab === 'packaging-types') {
        @if (packagingTypes.length === 0) {
          <div class="text-center py-4 text-muted small">
            <i class="bi bi-box d-block fs-3 mb-2 opacity-50"></i>
            No packaging types yet. Add one below.
          </div>
        } @else {
          <div class="manage-item-list mb-3">
            @for (pt of packagingTypes; track pt.id) {
              <div class="manage-item">
                <span class="fw-semibold">{{ pt.name }}</span>
                <button
                  type="button"
                  class="btn btn-action-delete btn-sm"
                  [disabled]="deletingPackagingTypeId === pt.id"
                  (click)="onDeletePackagingType(pt.id)">
                  @if (deletingPackagingTypeId === pt.id) {
                    <app-loading-spinner [inline]="true"></app-loading-spinner>
                  } @else {
                    <i class="bi bi-trash3"></i>
                  }
                </button>
              </div>
            }
          </div>
        }
        <div class="add-item-form">
          <p class="text-muted small fw-semibold mb-2">Add New Packaging Type</p>
          <div class="d-flex gap-2">
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="e.g. Bottle, Can, Drum"
              [(ngModel)]="newPackagingTypeName" />
            <button
              type="button"
              class="btn btn-primary btn-sm text-nowrap"
              [disabled]="!newPackagingTypeName.trim() || isCreatingPackagingType"
              (click)="onCreatePackagingType()">
              @if (isCreatingPackagingType) {
                <app-loading-spinner [inline]="true" [textClass]="'text-white'"></app-loading-spinner>
              } @else {
                <i class="bi bi-plus-lg"></i>
              }
            </button>
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>

<!-- Add Packaging Modal -->
<ng-template #addPackagingModal let-modal>
  <div class="modal-content">
    <form [formGroup]="packagingForm" (ngSubmit)="onSubmitPackaging()">
      <div class="modal-header">
        <h5 class="modal-title">{{ isEditing ? 'Edit' : 'New' }} Packaging Configuration</h5>
        <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
      </div>

      <div class="modal-body">
        <div class="row g-4">
          <div class="col-md-7">
            <label class="form-label">
              Packing Size & Unit
              <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <input type="number" formControlName="packing_size" class="form-control" placeholder="100" />
              <ng-select
                placeholder="Unit"
                [items]="units"
                formControlName="unit"
                bindValue="id"
                bindLabel="short_name"
                [clearable]="false"
                [searchable]="false"
                style="width: 100px" />
            </div>
          </div>

          <div class="col-md-5">
            <label class="form-label">
              Units per Box
              <span class="text-danger">*</span>
            </label>
            <input type="number" formControlName="nos" class="form-control" placeholder="10" />
          </div>

          <div class="col-md-12">
            <label class="form-label">Packaging Type</label>
            <ng-select
              placeholder="Select Type"
              [items]="packagingTypes"
              formControlName="packaging_type"
              bindValue="id"
              bindLabel="name"
              [clearable]="true"
              [searchable]="false" />
          </div>

          <div class="col-md-6">
            <label class="form-label">
              Dealer Rate (Excl. GST)
              <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <span class="input-group-text bg-light">₹</span>
              <input type="number" formControlName="dealer_rate" class="form-control" placeholder="0.00" />
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label">Dealer Rate (Incl. GST)</label>
            <div class="input-group">
              <span class="input-group-text bg-light">₹</span>
              <input type="text" formControlName="dealer_rate_incl_gst" class="form-control bg-light" readonly />
            </div>
          </div>

          <div class="col-md-6">
            <label class="form-label">
              GST Rate (%)
              <span class="text-danger">*</span>
            </label>
            <input type="number" formControlName="gst_rate" class="form-control" placeholder="18" />
          </div>

          <div class="col-md-6">
            <label class="form-label">
              MRP
              <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <span class="input-group-text bg-light">₹</span>
              <input type="number" formControlName="mrp" class="form-control" placeholder="0.00" />
            </div>
          </div>

          <div class="col-12">
            <label class="form-label">Packaging Visual</label>
            <div class="image-upload-wrapper">
              <input
                type="file"
                (change)="onImageSelected($event)"
                accept="image/*"
                id="packagingImage"
                class="d-none" />
              <label for="packagingImage" class="upload-zone">
                @if (imagePreview) {
                  <div class="preview-container">
                    <img [src]="imagePreview" alt="Preview" />
                    <button type="button" class="remove-img" (click)="onRemoveImage(); $event.preventDefault()">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                } @else {
                  <div class="upload-placeholder">
                    <i class="bi bi-camera"></i>
                    <span>Click to upload product image</span>
                  </div>
                }
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
        <button type="submit" class="btn btn-primary px-4" [disabled]="packagingForm.invalid || isSavingPackaging">
          @if (isSavingPackaging) {
            <app-loading-spinner [textClass]="'text-white'" [inline]="true" class="me-2"></app-loading-spinner>
            Saving...
          } @else {
            <i class="bi bi-box-arrow-down"></i>
            {{ isEditing ? 'Update' : 'Save' }}
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>
